import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Clock, CalendarDays, Search, Users, Heart, ArrowRight, LogIn, UserPlus, ExternalLink, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { photoUrl } from "@/lib/apiClient";
import { mapsUrl } from "@/lib/modules";
import { WeeklyEntry } from "@/components/WeeklyEntry";
import { RichText } from "@/components/RichText";
import { SupportButton } from "@/components/SupportButton";

function ContactDialog({ group }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { name, phone, group_id: group.id, message });
      toast.success("Recebemos seu contato! O líder falará com você em breve.");
      setOpen(false);
      setName(""); setPhone(""); setMessage("");
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1" data-testid="info-desk-contact-leader-button">
          Quero participar <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle>Falar com o líder · {group.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cn">Seu nome</Label>
            <Input id="cn" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="contact-name-input" />
          </div>
          <div>
            <Label htmlFor="cp">WhatsApp / Telefone</Label>
            <Input id="cp" value={phone} onChange={(e) => setPhone(e.target.value)} required className="mt-1.5" data-testid="contact-phone-input" />
          </div>
          <div>
            <Label htmlFor="cm">Mensagem (opcional)</Label>
            <Textarea id="cm" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5" data-testid="contact-message-input" />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="contact-submit-button">Enviar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [day, setDay] = useState("todos");
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    api.get("/groups", { params: { active_only: true } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/learn/highlight").then((r) => setHighlight(r.data)).catch(() => {});
  }, []);

  const days = ["todos", ...Array.from(new Set(groups.map((g) => g.day_of_week)))];
  const filtered = useMemo(
    () =>
      groups.filter((g) => {
        const matchesQ =
          !q ||
          g.name.toLowerCase().includes(q.toLowerCase()) ||
          g.neighborhood.toLowerCase().includes(q.toLowerCase()) ||
          g.category.toLowerCase().includes(q.toLowerCase());
        const matchesDay = day === "todos" || g.day_of_week === day;
        return matchesQ && matchesDay;
      }),
    [groups, q, day]
  );

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/70 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-2">
            <Link to="/oracao">
              <Button variant="ghost" size="sm" data-testid="header-prayer-button">Oração</Button>
            </Link>
            <Link to="/pesquisa">
              <Button variant="ghost" size="sm" data-testid="header-survey-button">Pesquisa</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="gap-1.5" data-testid="header-login-button">
                <LogIn className="h-4 w-4" /> Entrar
              </Button>
            </Link>
            <Link to="/login?tab=register">
              <Button size="sm" className="gap-1.5" data-testid="header-register-button">
                <UserPlus className="h-4 w-4" /> Cadastre-se
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <Badge className="mb-5 bg-accent/15 text-accent border-accent/30 hover:bg-accent/15">
              Balcão de Informações
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Encontre o seu <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">Pequeno Grupo</span>
            </h1>
            <p className="mt-5 text-muted-foreground text-lg max-w-xl leading-relaxed">
              Discipulado, comunhão e crescimento perto de você. Descubra um PGD no seu bairro e faça parte da família Aprisco Zona Norte.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> {groups.length} grupos ativos</div>
              <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-primary" /> Grupos de 3 a 24 pessoas</div>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_webapp-design-2/artifacts/bb6gdc5b_WhatsApp%20Image%202026-08-12%20at%2014.57.02.jpeg"
              alt="Aprisco Church — Zona Norte"
              className="rounded-2xl border border-border shadow-2xl object-cover w-full h-[380px]"
            />
          </div>
        </div>
      </section>

      {/* Estudo da semana + Visão (público) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <WeeklyEntry variant="home" />
      </section>

      {/* Estudo do mês em destaque */}
      {highlight && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-primary/5 p-6 sm:p-8" data-testid="study-highlight">
            <div className="flex items-center gap-2 text-accent mb-2">
              <BookOpen className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-widest">Estudo do Mês em Destaque</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">{highlight.title}</h2>
            {highlight.subtitle && <p className="text-primary mt-1">{highlight.subtitle}</p>}
            <Dialog>
              <DialogTrigger asChild>
                <Button className="mt-4 gap-2" data-testid="read-study-button">Ler completo <ArrowRight className="h-4 w-4" /></Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="study-dialog">
                <DialogHeader><DialogTitle>{highlight.title}</DialogTitle></DialogHeader>
                {highlight.subtitle && <p className="text-sm text-primary">{highlight.subtitle}</p>}
                {highlight.body ? <RichText>{highlight.body}</RichText> : <p className="text-sm text-muted-foreground">Conteúdo em breve.</p>}
              </DialogContent>
            </Dialog>
          </div>
        </section>
      )}

      {/* Finder */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por bairro, categoria ou nome do grupo…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="info-desk-search-input"
            />
          </div>
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger className="w-full sm:w-48" data-testid="info-desk-filter-day">
              <SelectValue placeholder="Dia" />
            </SelectTrigger>
            <SelectContent>
              {days.map((d) => (
                <SelectItem key={d} value={d}>{d === "todos" ? "Todos os dias" : d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((g) => (
            <div
              key={g.id}
              data-testid="info-desk-group-card"
              className="rounded-xl border border-border bg-card overflow-hidden flex flex-col hover:border-primary/50 transition-colors"
            >
              {g.photo_path && (
                <img src={photoUrl(g.photo_path)} alt={g.name} className="h-40 w-full object-cover" />
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-lg font-semibold">{g.name}</h3>
                  <Badge variant="secondary" className="shrink-0">{g.category}</Badge>
                </div>
                <div className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                  {(g.address || g.neighborhood) ? (() => {
                    const url = mapsUrl(g.address, g.neighborhood);
                    const label = [g.address, g.neighborhood].filter(Boolean).join(" · ");
                    return url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-start gap-2 text-primary hover:underline"
                        data-testid={`group-map-link-${g.id}`}>
                        <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{label} <ExternalLink className="inline h-3 w-3 mb-0.5" /></span>
                      </a>
                    ) : (
                      <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" /> {label}</div>
                    );
                  })() : (
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> A definir</div>
                  )}
                  <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> {g.day_of_week}{g.start_date ? ` · início em ${g.start_date.slice(8, 10)}/${g.start_date.slice(5, 7)}/${g.start_date.slice(0, 4)}` : ""}</div>
                  <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {g.time}</div>
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Líder: {g.leader_name || "A definir"}</div>
                </div>
                <div className="mt-5">
                  <ContactDialog group={g} />
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground">
              Nenhum grupo encontrado para esta busca.
            </div>
          )}
        </div>
      </section>
      <SupportButton />
    </div>
  );
}
